import React, { useState, useRef, useEffect, useMemo } from 'react'
import { Box, Typography } from '@mui/material'
import { useTranslation } from '../../../../../shared/hooks/useIntractionTranslate'
import { ArrowDownTrayIcon, ChevronDownIcon, MagnifyingGlassIcon, XMarkIcon } from '@heroicons/react/24/outline'
import { INTERAX_COLORS, INTERAX_TYPOGRAPHY } from '../../../../../constants/colors'
import { useGetSprints } from '../../../../../apiLayer'
import { useDebouncedValue } from '../../../../../shared/hooks/useDebouncedValue'
import { TrainingSprintConfig } from '../../types'
import { Sprint } from '../../../../../../../pure-js/types/Sprint'

const SPRINT_SEARCH_DEBOUNCE_MS = 300 // 300 ms

export interface CopyFromPreviousSprintProps {
  sprintData: TrainingSprintConfig
  onCopyParticipants: (sprint: Sprint) => void
  className?: string
}

export const CopyFromPreviousSprint: React.FC<CopyFromPreviousSprintProps> = ({
  sprintData,
  onCopyParticipants,
  className = ''
}) => {
  const { t } = useTranslation()
  const [selectedSprintId, setSelectedSprintId] = useState('')
  const [isOpen, setIsOpen] = useState(false)
  const [searchTerm, setSearchTerm] = useState('')
  const dropdownRef = useRef<HTMLDivElement>(null)

  // Search runs on the server (the list is paged), so debounce keystrokes instead of firing a
  // request per character.
  const debouncedSearchTerm = useDebouncedValue(searchTerm, SPRINT_SEARCH_DEBOUNCE_MS)
  const { data: sprintsResponse, isLoading, error } = useGetSprints({ search: debouncedSearchTerm })
  const { sprints } = sprintsResponse || {}

  // The wizard tracks the in-progress sprint's id under `sprintData.sprintId` (set after the first
  // draft save); `id` is a fallback for callers that use it. Exclude it so you can't copy from the
  // sprint you're currently creating/editing.
  const currentSprintId = (sprintData.sprintId as string | undefined) ?? (sprintData.id as string | undefined)

  // The server has already applied the search; only the sprint being edited is dropped here.
  const previousSprints: Sprint[] = useMemo(
    () => (sprints || []).filter((sprint) => sprint.id !== currentSprintId),
    [sprints, currentSprintId]
  )

  // Close when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false)
      }
    }
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside)
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [isOpen])

  const handleToggle = () => {
    if (!isLoading) {
      if (!isOpen) setSearchTerm('')
      setIsOpen((prev) => !prev)
    }
  }

  const handleSelect = (sprintId: string) => {
    setSelectedSprintId(sprintId)
    setIsOpen(false)
    setSearchTerm('')
  }

  const handleCopyParticipants = () => {
    const selectedSprint = previousSprints.find((sprint) => sprint.id === selectedSprintId)
    if (selectedSprint) {
      onCopyParticipants(selectedSprint)
      setSelectedSprintId('')
    }
  }

  const selectedSprint = previousSprints.find((sprint) => sprint.id === selectedSprintId)

  const triggerLabel = isLoading
    ? t('facilitator.trainingSprint.addParticipants.loadingSprints')
    : selectedSprint
      ? `${selectedSprint.title} (${selectedSprint.participants.length})`
      : t('facilitator.trainingSprint.addParticipants.choosePreviousSprint')

  return (
    <div
      className={`p-6 rounded-lg border ${className}`}
      style={{
        backgroundColor: INTERAX_COLORS.heritageBlue40,
        borderColor: INTERAX_COLORS.heritageBlue
      }}
    >
      {/* Section title */}
      <Typography
        sx={{
          ...INTERAX_TYPOGRAPHY.headline4,
          color: INTERAX_COLORS.white,
          mb: 2
        }}
      >
        {t('facilitator.trainingSprint.addParticipants.copyFromPreviousSprint')}
      </Typography>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {/* Label */}
        <Typography
          component="label"
          sx={{
            ...INTERAX_TYPOGRAPHY.body3,
            color: INTERAX_COLORS.thunderGrey,
            display: 'block',
            mb: -1
          }}
        >
          {t('facilitator.trainingSprint.addParticipants.selectSprint')}
        </Typography>

        {/* Custom searchable dropdown */}
        <Box ref={dropdownRef} sx={{ position: 'relative' }}>
          {/* Trigger button */}
          <Box
            component="button"
            type="button"
            onClick={handleToggle}
            disabled={isLoading}
            data-testid="previous-sprint-select"
            aria-haspopup="listbox"
            aria-expanded={isOpen}
            sx={{
              width: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 1,
              px: 1.5,
              py: 1.25,
              borderRadius: '8px',
              border: `2px solid ${isOpen ? INTERAX_COLORS.sun : INTERAX_COLORS.thunderGrey}`,
              backgroundColor: INTERAX_COLORS.offBlack,
              cursor: isLoading ? 'not-allowed' : 'pointer',
              opacity: isLoading ? 0.6 : 1,
              appearance: 'none',
              transition: 'border-color 0.2s ease',
              '&:hover:not(:disabled)': {
                borderColor: INTERAX_COLORS.sun
              }
            }}
          >
            <Typography
              sx={{
                ...INTERAX_TYPOGRAPHY.body3,
                color: selectedSprint ? INTERAX_COLORS.white : INTERAX_COLORS.thunderGrey,
                flex: 1,
                textAlign: 'left',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap'
              }}
            >
              {triggerLabel}
            </Typography>
            <ChevronDownIcon
              style={{
                width: 18,
                height: 18,
                color: INTERAX_COLORS.thunderGrey,
                transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                transition: 'transform 0.2s ease',
                flexShrink: 0
              }}
            />
          </Box>

          {/* Dropdown panel */}
          {isOpen && (
            <Box
              role="listbox"
              sx={{
                position: 'absolute',
                top: 'calc(100% + 4px)',
                left: 0,
                right: 0,
                zIndex: 1200,
                backgroundColor: INTERAX_COLORS.offBlack,
                border: `1px solid ${INTERAX_COLORS.thunderGrey}`,
                borderRadius: '8px',
                boxShadow: '0 8px 24px rgba(0,0,0,0.4)',
                overflow: 'hidden'
              }}
            >
              {/* Search input */}
              <Box
                sx={{
                  px: 1.5,
                  py: 1,
                  borderBottom: `1px solid rgba(120,144,156,0.2)`
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    px: 1,
                    py: 0.75,
                    borderRadius: '6px',
                    backgroundColor: INTERAX_COLORS.heritageBlue
                  }}
                >
                  <MagnifyingGlassIcon
                    style={{ width: 16, height: 16, color: INTERAX_COLORS.thunderGrey, flexShrink: 0 }}
                  />
                  <Box
                    component="input"
                    type="search"
                    value={searchTerm}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearchTerm(e.target.value)}
                    placeholder={t('facilitator.trainingSprint.addParticipants.searchSprints')}
                    data-testid="sprint-search-input"
                    autoFocus
                    sx={{
                      flex: 1,
                      border: 'none',
                      outline: 'none',
                      background: 'transparent',
                      ...INTERAX_TYPOGRAPHY.body3,
                      color: INTERAX_COLORS.white,
                      '&::placeholder': { color: INTERAX_COLORS.thunderGrey },
                      '&::-webkit-search-cancel-button': { display: 'none' }
                    }}
                  />
                  {searchTerm && (
                    <Box
                      component="button"
                      type="button"
                      onClick={() => setSearchTerm('')}
                      sx={{
                        appearance: 'none',
                        border: 'none',
                        background: 'transparent',
                        cursor: 'pointer',
                        p: 0,
                        display: 'flex',
                        alignItems: 'center'
                      }}
                    >
                      <XMarkIcon style={{ width: 14, height: 14, color: INTERAX_COLORS.thunderGrey }} />
                    </Box>
                  )}
                </Box>
              </Box>

              {/* Sprint list */}
              <Box
                sx={{
                  maxHeight: '240px',
                  overflowY: 'auto',
                  '&::-webkit-scrollbar': { display: 'none' },
                  scrollbarWidth: 'none'
                }}
              >
                {previousSprints.length === 0 ? (
                  <Typography
                    sx={{
                      ...INTERAX_TYPOGRAPHY.body3,
                      color: INTERAX_COLORS.thunderGrey,
                      px: 2,
                      py: 1.5
                    }}
                  >
                    {t('facilitator.trainingSprint.addParticipants.noPreviousSprints')}
                  </Typography>
                ) : (
                  previousSprints.map((sprint, index) => (
                    <React.Fragment key={sprint.id}>
                      <Box
                        component="button"
                        type="button"
                        role="option"
                        aria-selected={sprint.id === selectedSprintId}
                        onClick={() => handleSelect(sprint.id)}
                        sx={{
                          width: '100%',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          appearance: 'none',
                          border: 'none',
                          px: 2,
                          py: 1,
                          cursor: 'pointer',
                          backgroundColor:
                            sprint.id === selectedSprintId ? INTERAX_COLORS.heritageBlue40 : 'transparent',
                          transition: 'background-color 0.15s ease',
                          '&:hover': { backgroundColor: INTERAX_COLORS.heritageBlue40 }
                        }}
                      >
                        <Typography
                          sx={{
                            ...INTERAX_TYPOGRAPHY.body3,
                            color: INTERAX_COLORS.white,
                            textAlign: 'left',
                            flex: 1,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap'
                          }}
                        >
                          {sprint.title}
                        </Typography>
                        <Typography
                          sx={{
                            ...INTERAX_TYPOGRAPHY.body3,
                            color: INTERAX_COLORS.thunderGrey,
                            fontSize: '11px',
                            ml: 1.5,
                            flexShrink: 0
                          }}
                        >
                          {sprint.participants.length}p / {sprint.teams.length}t
                        </Typography>
                      </Box>
                      {index < previousSprints.length - 1 && (
                        <Box
                          sx={{
                            height: '1px',
                            backgroundColor: INTERAX_COLORS.thunderGrey,
                            opacity: 0.15,
                            mx: 1
                          }}
                        />
                      )}
                    </React.Fragment>
                  ))
                )}
              </Box>
            </Box>
          )}
        </Box>

        {/* Preview panel */}
        {selectedSprint && (
          <Box sx={{ p: 1.5, borderRadius: '8px', backgroundColor: INTERAX_COLORS.heritageBlue40 }}>
            <Typography sx={{ ...INTERAX_TYPOGRAPHY.body3, color: INTERAX_COLORS.white }}>
              {t('facilitator.trainingSprint.addParticipants.sprintPreview', {
                participants: selectedSprint.participants.length,
                teams: selectedSprint.teams.length
              })}
            </Typography>
          </Box>
        )}

        {/* Error panel */}
        {error && (
          <Box sx={{ p: 1.5, borderRadius: '8px', backgroundColor: INTERAX_COLORS.redErrorBackground }}>
            <Typography sx={{ ...INTERAX_TYPOGRAPHY.body3, color: INTERAX_COLORS.redError }}>
              {t('facilitator.trainingSprint.addParticipants.errorFetchingSprints')}
            </Typography>
          </Box>
        )}

        {/* Copy button */}
        <button
          onClick={handleCopyParticipants}
          disabled={!selectedSprintId || isLoading}
          className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-lg font-semibold transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          data-testid="copy-participants-btn"
          style={{
            backgroundColor: INTERAX_COLORS.sun,
            color: INTERAX_COLORS.offBlack,
            fontFamily: INTERAX_TYPOGRAPHY.body1.fontFamily,
            fontWeight: INTERAX_TYPOGRAPHY.body1.fontWeight,
            fontSize: INTERAX_TYPOGRAPHY.body1.fontSize,
            lineHeight: INTERAX_TYPOGRAPHY.body1.lineHeight
          }}
        >
          <ArrowDownTrayIcon className="w-5 h-5" />
          {t('facilitator.trainingSprint.addParticipants.copyParticipants')}
        </button>
      </Box>
    </div>
  )
}
